import type { RepositoryInventory } from "../intake/types";

import type { DiagnosticFinding } from "./types";

function finding(value: DiagnosticFinding): DiagnosticFinding {
  return value;
}

export function diagnoseRepositoryInventory(
  inventory: RepositoryInventory,
): DiagnosticFinding[] {
  const findings: DiagnosticFinding[] = [];

  if (inventory.specs.length === 0) {
    findings.push(
      finding({
        id: "spec-missing",
        category: "spec",
        severity: "high",
        confidence: "confirmed",
        title: "Canonical specification not detected",
        description:
          "The repository sweep did not identify a canonical specification artifact.",
        evidence: [],
        affectedPaths: [],
        recommendedAction:
          "Establish or identify the canonical project specification before autonomous mutation.",
        requiresLiveVerification: false,
        requiresMutation: true,
      }),
    );
  }

  if (inventory.tests.length === 0) {
    findings.push(
      finding({
        id: "tests-missing",
        category: "testing",
        severity: "high",
        confidence: "confirmed",
        title: "Automated tests not detected",
        description:
          "No automated test files were identified during repository intake.",
        evidence: [],
        affectedPaths: [],
        recommendedAction:
          "Create a baseline regression suite before autonomous repair.",
        requiresLiveVerification: false,
        requiresMutation: true,
      }),
    );
  }

  if (inventory.workflows.length === 0) {
    findings.push(
      finding({
        id: "workflow-missing",
        category: "deployment",
        severity: "medium",
        confidence: "confirmed",
        title: "CI/CD workflow not detected",
        description: "No repository workflow was detected during intake.",
        evidence: [],
        affectedPaths: [],
        recommendedAction:
          "Determine the authoritative build and deployment path before production mutation.",
        requiresLiveVerification: true,
        requiresMutation: false,
      }),
    );
  }

  for (const integration of inventory.integrations) {
    findings.push(
      finding({
        id: `integration-${integration.integration.toLowerCase()}`,
        category: "integration",
        severity: "info",
        confidence: "strong",
        title: `${integration.integration} integration detected`,
        description: `Repository evidence indicates integration with ${integration.integration}.`,
        evidence: integration.evidence.map((path) => ({
          path,
          reason: `Evidence for ${integration.integration} integration.`,
        })),
        affectedPaths: [...integration.evidence],
        recommendedAction: integration.requiresCapability
          ? `Resolve capability ${integration.requiresCapability} before live inspection.`
          : "Determine whether live inspection capability is required.",
        requiresLiveVerification: true,
        requiresMutation: false,
      }),
    );
  }

  const unknownArtifacts = inventory.artifacts.filter(
    (artifact) => artifact.kind === "unknown",
  );

  if (unknownArtifacts.length > 0) {
    findings.push(
      finding({
        id: "unknown-artifacts",
        category: "unknown",
        severity: "info",
        confidence: "confirmed",
        title: "Unclassified repository artifacts exist",
        description: `${unknownArtifacts.length} repository artifact(s) could not be classified by the current intake rules.`,
        evidence: unknownArtifacts.slice(0, 50).map((artifact) => ({
          path: artifact.path,
          reason: "Artifact remained unknown after repository classification.",
        })),
        affectedPaths: unknownArtifacts.map((artifact) => artifact.path),
        recommendedAction:
          "Inspect unknown artifacts and extend classification only when evidence supports it.",
        requiresLiveVerification: false,
        requiresMutation: false,
      }),
    );
  }

  return findings;
}
